import {
  OnPaymentMethodCallbackErrorHandler,
  OnPaymentMethodCallbackSuccessHandler,
  PaymentCallbackError,
  PaymentMethodCallbackTrackingData,
} from 'apps-common/components/PaymentCallbackPage';
import { createSubscription } from 'apps-common/graphql/createSubscription';
import { renewSubscription } from 'apps-common/graphql/renewSubscription';
import { updateAccount } from 'apps-common/graphql/updateAccount';
import { AppType, BillingPeriod, FlowType, PaymentMethodType, UserAddressForm } from 'apps-common/types';
import { convertTrackingPaymentMethodTypeToPaymentMethodType, track } from 'apps-common/utils/analytics';
import { isCustomError } from 'apps-common/utils/errorHandler';
import { logger } from 'apps-common/utils/logger';
import { GatewayError, isGatewayError } from 'apps-common/utils/SupaError';
import { isSupaGQLError } from 'apps-common/utils/SupaGraphQLError';

const redirect = (route: string) => {
  window.parent.location.replace(route);
};

export const onPaymentMethodCallbackErrorHandler: OnPaymentMethodCallbackErrorHandler = (
  paymentMethodRoute: string,
  error: PaymentCallbackError,
  appType: AppType,
  paymentMethodCallbackTrackingData: PaymentMethodCallbackTrackingData,
) => {
  const failedEvent = appType === 'signup' ? 'Adding Payment Method Failed' : 'Updating Payment Method Failed';
  const logMessage = appType === 'signup' ? 'Error creating subscription' : 'Error updating payment method';
  track({
    event: failedEvent,
    payload: {
      reason: error.message ?? 'Unknown error',
      ...paymentMethodCallbackTrackingData,
    },
  });

  logger.error(logMessage, { error, paymentMethodCallbackTrackingData });
  redirect(paymentMethodRoute);
};

const signupErrorHandler = (
  paymentMethodRoute: string,
  paymentMethodCallbackTrackingData: PaymentMethodCallbackTrackingData,
  error: PaymentCallbackError | GatewayError,
) => {
  const handledSupaGQLErrors = ['SUBSCRIPTION_ALREADY_EXISTS', 'RESOURCE_LOCK_ALREADY_ACQUIRED'];
  const handledCustomErrors = ['membership_error_no_subscription_created'];

  const shouldSuppressError =
    (isSupaGQLError(error) && handledSupaGQLErrors.includes(error.code)) ||
    (isCustomError(error) && handledCustomErrors.includes(error.message));
  if (!shouldSuppressError) {
    return onPaymentMethodCallbackErrorHandler(paymentMethodRoute, error, 'signup', paymentMethodCallbackTrackingData);
  }
};

const renewErrorHandler = (
  paymentMethodRoute: string,
  paymentMethodCallbackTrackingData: PaymentMethodCallbackTrackingData,
  error: PaymentCallbackError | GatewayError,
) => {
  const handledSupaGQLErrors = ['PAYMENT_GATEWAY', 'RESOURCE_LOCK_ALREADY_ACQUIRED'];
  const handledCustomErrors = ['membership_error_on_account_update', 'membership_error_no_subscription_created'];

  const shouldSuppressError =
    (isSupaGQLError(error) && handledSupaGQLErrors.includes(error.code)) ||
    isGatewayError(error) ||
    (isCustomError(error) && handledCustomErrors.includes(error.message));

  if (shouldSuppressError) {
    return onPaymentMethodCallbackErrorHandler(paymentMethodRoute, error, 'hub', paymentMethodCallbackTrackingData);
  }

  logger.error(`Error in ${paymentMethodCallbackTrackingData.paymentMethodType} callback`, {
    error,
    paymentMethodCallbackTrackingData,
  });
  redirect('/error');
};

const logGatewayError = (type: PaymentMethodType) => (error: unknown) => {
  if ((isSupaGQLError(error) && error.code === 'PAYMENT_GATEWAY') || isGatewayError(error)) {
    const { message, code } = error;
    logger.error(`GatewayError in ${type} callback`, {
      message,
      code,
    });
  }

  throw error;
};

const signupFlow = async (
  paymentMethodType: PaymentMethodType,
  paymentMethodId: string,
  paymentGatewayName: string,
  userAddressForm: UserAddressForm,
  billingPeriod: BillingPeriod,
  email: string,
  paymentMethodRoute: string,
  paymentMethodCallbackTrackingData: PaymentMethodCallbackTrackingData,
) => {
  try {
    logger.debug(`${paymentMethodType} callback create subscription`, { paymentMethodId });

    await createSubscription(paymentMethodId, userAddressForm, paymentGatewayName, billingPeriod, email).catch(
      logGatewayError(paymentMethodType),
    );

    logger.debug(`${paymentMethodType} callback subscription created successfully`, {
      paymentMethodCallbackTrackingData,
    });
    track({
      event: 'Subscription Created',
      payload: {
        cta: 'save_exit / checkout',
        action: 'onboarding_complete',
        ...paymentMethodCallbackTrackingData,
      },
    });
    logger.info(`${paymentMethodType} callback success, redirecting to complete page`);
  } catch (error) {
    const err: PaymentCallbackError | GatewayError = PaymentCallbackError.is(error) ? error : (error as GatewayError);
    return signupErrorHandler(paymentMethodRoute, paymentMethodCallbackTrackingData, err);
  }
};

const renewFlow = async (
  paymentMethodType: PaymentMethodType,
  billingPeriod: BillingPeriod,
  paymentMethodRoute: string,
  paymentMethodCallbackTrackingData: PaymentMethodCallbackTrackingData,
) => {
  try {
    logger.debug(`${paymentMethodType} callback renewing subscription`, {
      flowType: FlowType.Renew,
    });

    await renewSubscription(billingPeriod).catch(logGatewayError(paymentMethodType));

    logger.debug(`${paymentMethodType} callback renew successful`, {
      paymentMethodCallbackTrackingData,
    });

    track({
      event: 'Subscription Renewed',
      payload: {
        ...paymentMethodCallbackTrackingData,
      },
    });
  } catch (error) {
    const err: PaymentCallbackError | GatewayError = PaymentCallbackError.is(error) ? error : (error as GatewayError);
    return renewErrorHandler(paymentMethodRoute, paymentMethodCallbackTrackingData, err);
  }
};

const updateAccountFlow = async (
  paymentMethodType: PaymentMethodType,
  paymentMethodId: string,
  paymentGatewayName: string,
  userAddressForm: UserAddressForm,
  email: string,
  paymentMethodRoute: string,
  paymentMethodCallbackTrackingData: PaymentMethodCallbackTrackingData,
) => {
  try {
    logger.debug(`${paymentMethodType} callback updating account`);

    await updateAccount(paymentMethodId, paymentGatewayName, userAddressForm, email).catch(
      logGatewayError(paymentMethodType),
    );

    logger.debug(`${paymentMethodType} callback updating account successful`, {
      paymentMethodCallbackTrackingData,
    });

    track({
      event: 'Payment Method Updated',
      payload: {
        ...paymentMethodCallbackTrackingData,
      },
    });
  } catch (error) {
    const err: PaymentCallbackError | GatewayError = PaymentCallbackError.is(error) ? error : (error as GatewayError);
    return renewErrorHandler(paymentMethodRoute, paymentMethodCallbackTrackingData, err);
  }
};

export const onPaymentMethodCallbackSuccessHandler: OnPaymentMethodCallbackSuccessHandler = async (
  flowType: FlowType,
  email: string,
  userAddressForm: UserAddressForm,
  paymentMethodId: string,
  completeRoute: string,
  paymentMethodRoute: string,
  paymentMethodCallbackTrackingData: PaymentMethodCallbackTrackingData,
) => {
  const paymentMethodType = convertTrackingPaymentMethodTypeToPaymentMethodType(
    paymentMethodCallbackTrackingData.paymentMethodType,
  );

  switch (flowType) {
    case FlowType.Signup:
      await signupFlow(
        paymentMethodType,
        paymentMethodId,
        paymentMethodCallbackTrackingData.paymentGateway,
        userAddressForm,
        paymentMethodCallbackTrackingData.billingPeriod,
        email,
        paymentMethodRoute,
        paymentMethodCallbackTrackingData,
      );
      break;
    case FlowType.AddOrUpdatePaymentMethod:
      await updateAccountFlow(
        paymentMethodType,
        paymentMethodId,
        paymentMethodCallbackTrackingData.paymentGateway,
        userAddressForm,
        email,
        paymentMethodRoute,
        paymentMethodCallbackTrackingData,
      );
      logger.info(`${paymentMethodType} callback success, redirecting to success page`, {
        flow: flowType,
      });
      break;
    case FlowType.Renew:
      await updateAccountFlow(
        paymentMethodType,
        paymentMethodId,
        paymentMethodCallbackTrackingData.paymentGateway,
        userAddressForm,
        email,
        paymentMethodRoute,
        paymentMethodCallbackTrackingData,
      );
      await renewFlow(
        paymentMethodType,
        paymentMethodCallbackTrackingData.billingPeriod,
        paymentMethodRoute,
        paymentMethodCallbackTrackingData,
      );
      logger.info(`${paymentMethodType} callback success, redirecting to success page`, {
        flow: flowType,
      });
      break;
  }

  redirect(completeRoute);
};
